import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import auctionService from '../services/auctionService';
import { formatCurrency, daysRemaining, formatDate } from '../utils/validators';
import './AuctionDetail.css';

const AuctionDetail = () => {
  const { auctionId } = useParams();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [action, setAction] = useState('');

  useEffect(() => {
    const fetchAuction = async () => {
      const [auctionRes, bidsRes] = await Promise.all([
        auctionService.getAuction(auctionId),
        auctionService.getAuctionBids(auctionId),
      ]);

      if (auctionRes.success) {
        setAuction(auctionRes.data);
      } else {
        setError(auctionRes.error);
      }

      if (bidsRes.success) {
        setBids(bidsRes.data.bids || []);
      }
      setLoading(false);
    };

    fetchAuction();
  }, [auctionId]);

  const handlePauseAuction = async () => {
    setAction('pause');
    const result = await auctionService.pauseAuction(auctionId);
    if (result.success) {
      setAuction({ ...auction, status: 'paused' });
    } else {
      setError(result.error);
    }
    setAction('');
  };

  const handleResumeAuction = async () => {
    setAction('resume');
    const result = await auctionService.resumeAuction(auctionId);
    if (result.success) {
      setAuction({ ...auction, status: 'active' });
    } else {
      setError(result.error);
    }
    setAction('');
  };

  const handleExtendAuction = async (hours) => {
    setAction('extend');
    const result = await auctionService.extendAuction(auctionId, hours);
    if (result.success) {
      setAuction(result.data);
    } else {
      setError(result.error);
    }
    setAction('');
  };

  if (loading) {
    return <div className="container py-4"><div className="spinner"><div className="loading"></div>Loading...</div></div>;
  }

  if (!auction) {
    return <div className="container py-4"><div className="alert alert-danger">Auction not found</div></div>;
  }

  return (
    <div className="container py-4">
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="grid grid-2">
        {/* Photos */}
        <div className="card">
          <div className="photo-gallery">
            {auction.photos && auction.photos.length > 0 ? (
              <>
                <img
                  src={auction.photos[0]}
                  alt="Vehicle"
                  className="main-photo"
                />
                {auction.photos.length > 1 && (
                  <div className="photo-thumbnails">
                    {auction.photos.map((photo, idx) => (
                      <img key={idx} src={photo} alt={`Thumbnail ${idx}`} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="no-photo">No photos</div>
            )}
          </div>
        </div>

        {/* Auction Info */}
        <div>
          <div className="card">
            <h2>{auction.vehicle.year} {auction.vehicle.make} {auction.vehicle.model}</h2>
            <p className="text-muted">{auction.vehicle.vin}</p>

            <div className="info-grid">
              <div className="info-item">
                <span>Status</span>
                <strong className={`badge badge-${auction.status === 'active' ? 'success' : 'warning'}`}>
                  {auction.status}
                </strong>
              </div>
              <div className="info-item">
                <span>Current Bid</span>
                <strong>{formatCurrency(auction.currentBid || auction.reservePrice)}</strong>
              </div>
              <div className="info-item">
                <span>Reserve Price</span>
                <strong>{formatCurrency(auction.reservePrice)}</strong>
              </div>
              <div className="info-item">
                <span>Total Bids</span>
                <strong>{auction.bidCount || 0}</strong>
              </div>
              <div className="info-item">
                <span>Ends In</span>
                <strong>{daysRemaining(auction.endTime)}</strong>
              </div>
              <div className="info-item">
                <span>Mileage</span>
                <strong>{auction.vehicle.mileage?.toLocaleString()} miles</strong>
              </div>
            </div>

            <div className="actions-group">
              {auction.status === 'active' && (
                <>
                  <button
                    onClick={handlePauseAuction}
                    disabled={action === 'pause'}
                    className="btn btn-warning"
                  >
                    Pause Auction
                  </button>
                  <button
                    onClick={() => handleExtendAuction(24)}
                    disabled={action === 'extend'}
                    className="btn btn-secondary"
                  >
                    + 1 Day
                  </button>
                </>
              )}
              {auction.status === 'paused' && (
                <button
                  onClick={handleResumeAuction}
                  disabled={action === 'resume'}
                  className="btn btn-success"
                >
                  Resume Auction
                </button>
              )}
            </div>
          </div>

          {/* Condition Assessment */}
          <div className="card mt-3">
            <h3>Condition</h3>
            <div className="condition-summary">
              <div className="condition-row">
                <span>Exterior</span>
                <div className="rating">{auction.condition?.exterior || 0}/10</div>
              </div>
              <div className="condition-row">
                <span>Interior</span>
                <div className="rating">{auction.condition?.interior || 0}/10</div>
              </div>
              <div className="condition-row">
                <span>Mechanical</span>
                <div className="rating">{auction.condition?.mechanical || 0}/10</div>
              </div>
            </div>
            {auction.condition?.notes && (
              <div className="notes-section">
                <strong>Notes:</strong>
                <p>{auction.condition.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bids Table */}
      <div className="card mt-3">
        <h3>Bids ({bids.length})</h3>
        {bids.length === 0 ? (
          <p className="text-muted">No bids yet</p>
        ) : (
          <div className="bids-list">
            {bids.map((bid, idx) => (
              <div key={idx} className="bid-item">
                <div>
                  <strong>{bid.buyerName}</strong>
                  <p className="text-muted">{bid.buyerId}</p>
                </div>
                <div>
                  <strong>{formatCurrency(bid.amount)}</strong>
                  <p className="text-muted">{formatDate(bid.createdAt, 'time')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuctionDetail;
